import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Grid, 
  Card, 
  CardContent,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Inventory as InventoryIcon,
  Notifications as AlertIcon,
  EventAvailable as ExpiryIcon,
  Category as CategoryIcon,
  Login as LoginIcon,
  PersonAdd as RegisterIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

function HomePage() {
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Paper 
        elevation={0}
        sx={{ 
          py: { xs: 6, md: 12 },
          px: 4,
          mb: 6,
          borderRadius: 0,
          background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              fontWeight="bold"
              sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}
            >
              Welcome to HouseHoldr
            </Typography>
            
            <Typography 
              variant="h5" 
              component="p" 
              gutterBottom
              sx={{ mb: 4, opacity: 0.9 }}
            >
              Your personal inventory management solution
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'center',
                flexDirection: { xs: 'column', sm: 'row' }
              }}
            >
              {isAuthenticated ? (
                <Button
                  component={RouterLink}
                  to="/inventory"
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<DashboardIcon />}
                  sx={{ 
                    px: 4, 
                    py: 1.5,
                    fontWeight: 'bold',
                    boxShadow: theme.shadows[8],
                    '&:hover': {
                      boxShadow: theme.shadows[12],
                    }
                  }}
                >
                  Go to My Inventory
                </Button>
              ) : (
                <>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<LoginIcon />}
                    sx={{ 
                      px: 4, 
                      py: 1.5,
                      fontWeight: 'bold',
                      boxShadow: theme.shadows[8],
                      '&:hover': {
                        boxShadow: theme.shadows[12],
                      }
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="outlined"
                    color="inherit"
                    size="large"
                    startIcon={<RegisterIcon />}
                    sx={{ 
                      px: 4, 
                      py: 1.5,
                      fontWeight: 'bold',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                      }
                    }}
                  >
                    Create Account
                  </Button>
                </>
              )}
            </Box>
          </motion.div>
        </Container>
      </Paper>
      
      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            fontWeight="bold"
            textAlign="center"
            sx={{ mb: 6 }}
          >
            Features
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mb: 2 
                      }}
                    >
                      <InventoryIcon 
                        sx={{ 
                          fontSize: 60, 
                          color: theme.palette.primary.main 
                        }} 
                      />
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                      Track Your Items
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Keep track of all your household items in one place. Never run out of essentials again.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mb: 2 
                      }}
                    >
                      <AlertIcon 
                        sx={{ 
                          fontSize: 60, 
                          color: theme.palette.warning.main 
                        }} 
                      />
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                      Low Stock Alerts
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Set thresholds for your items and get notified when you're running low.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mb: 2 
                      }}
                    >
                      <ExpiryIcon 
                        sx={{ 
                          fontSize: 60, 
                          color: theme.palette.error.main 
                        }} 
                      />
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                      Expiry Tracking
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Never waste food or products again. Track expiration dates and use items before they go bad.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={itemVariants}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: theme.shadows[8],
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        mb: 2 
                      }}
                    >
                      <CategoryIcon 
                        sx={{ 
                          fontSize: 60, 
                          color: theme.palette.success.main 
                        }} 
                      />
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                      Organize By Category
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Easily organize your inventory by category, location, or other criteria for quick access.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}

export default HomePage;